import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../Style/Bstory.css';
import { useLanguage } from '../Context/Languagecontext';

// ── Image Asset Manifest Imports ──
import arunPhoto1 from '../Assets/journey1.webp';
import arunPhoto2 from '../Assets/journey2.webp';
import arunPhoto3 from '../Assets/journey3.webp';
import arunPhoto4 from '../Assets/journey4.webp';

export default function BStory() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const { t, lang } = useLanguage();
  const storyModule = t.businessStory;

  const animating = useRef(false);
  const headerRef = useRef(null);
  const cardRef = useRef(null);

  // Static array grouping required image resources
  const photosList = useMemo(() => [
    arunPhoto1,
    arunPhoto2,
    arunPhoto3,
    arunPhoto4
  ], []);

  /* ── Structural Assembly of Dynamic Localized Content Slides ── */
  const stories = useMemo(() => {
    const rawSlides = storyModule?.slides || [];
    const totalCount = rawSlides.length;
    
    return rawSlides.map((slide, index) => ({
      ...slide,
      // Format the incremental fractional pagination indices string dynamically
      num: `${String(index + 1).padStart(2, '0')} / ${String(totalCount).padStart(2, '0')}`,
      img: photosList[index] || arunPhoto1
    }));
  }, [storyModule, photosList]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal');
          }
        });
      },
      { threshold: 0.2 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    if (cardRef.current) observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  const goTo = (idx) => {
    if (animating.current || idx === current || !stories.length) return;

    animating.current = true;
    setIsAnimating(true);

    setTimeout(() => {
      setCurrent(idx);

      setTimeout(() => {
        setIsAnimating(false);
        animating.current = false;
      }, 450);
    }, 220);
  };

  const next = () => {
    if (!stories.length) return;
    goTo((current + 1) % stories.length);
  };
  
  const prev = () => {
    if (!stories.length) return;
    goTo((current - 1 + stories.length) % stories.length);
  };

  if (!stories.length || !stories[current]) return null;
  const slide = stories[current];

  return (
    <section className={`bstory-section ${lang === 'ta' ? 'bstory-section--tamil' : ''}`}>
      <div className="bstory-inner">

        <div className="bstory-header" ref={headerRef}>
          <h2 className="bstory-heading">{storyModule?.heading}</h2>
        </div>

        <div className="bstory-card" ref={cardRef}>

          {/* Left — Text Content */}
          <div className="bstory-content">
            <div className={`bstory-text-slide ${isAnimating ? 'exit' : 'enter'}`}>
              <span className="bstory-slide-num">{slide.num}</span>

              <span className="bstory-quote-icon" aria-hidden="true">
                &ldquo;
              </span>

              <h3 className="bstory-name">
                {slide.title}
              </h3>

              <p className="bstory-role">
                {slide.role}
              </p>

              <p className="bstory-body">
                {slide.body}
              </p>
            </div>

            {/* Navigation Elements */}
            <div className="bstory-nav">
              <button
                className="bstory-arrow"
                onClick={prev}
                aria-label="Previous story slide"
              >
                &#8249;
              </button>

              <div className="bstory-dots" role="tablist">
                {stories.map((_, i) => (
                  <button
                    key={i}
                    className={`bstory-dot ${i === current ? 'active' : ''}`}
                    onClick={() => goTo(i)}
                    role="tab"
                    aria-selected={i === current}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>

              <button
                className="bstory-arrow"
                onClick={next}
                aria-label="Next story slide"
              >
                &#8250;
              </button>
            </div>
          </div>

          {/* Right — Photo Frame */}
          <div className="bstory-photo-wrap">
            <img
              src={slide.img}
              alt={slide.title}
              className={`bstory-photo ${isAnimating ? 'img-exit' : 'img-enter'}`}
            />
          </div>

        </div>
      </div>
    </section>
  );
}